import React, { useEffect, useState } from "react";
import { statefulAnalysisTree } from "../../services/AnalysisTreeServices";
import { CommentaryContextProvider } from "../../context/CommentaryContext";
import { useAnalysisTreeContext } from "../../context/AnalysisTreeContext";
import AnalysisPanel from "../../components/analyse/AnalysisPanel";
import { useUser } from "../../context/UserContext";
import { StatefulRequestBuilder } from "../stateful/StatefulRequests";
import showToast from "../../hooks/useCustomToast";

interface AnalysisPanelContainerProps {
  onToggleChat: () => void;
  isChatOpen: boolean;
}
export type View = "table" | "chart" | "prompt";

const AnalysisPanelContainer: React.FC<AnalysisPanelContainerProps> = ({ onToggleChat, isChatOpen }) => {

  const { isAdmin } = useUser();
  const { 
    nodeDataLoader, 
    selectedNode, 
    tree, 
    treePannelLoader, 
    transactionId,
    setSelectedNodesToShowTables,
    selectedNodesToShowTables,
    isCommentaryVisible,
    setIsCommentaryVisible,
    nodeInfoMap
  } = useAnalysisTreeContext();

  const [analysisName, setAnalysisName] = useState<string>(tree?.header?.name!);
  const [analysisDescription, setAnalysisDescription] = useState<string>(tree?.header?.description || "");
  
  const [showTable, setShowTable] = useState<boolean>(false);
  const [isClearAllTables, setIsClearAllTables] = useState<boolean>(false);
  
  // Add this state to track which nodes have tables open and their indices
  const [activeDataTables, setActiveDataTables] = useState<Map<string, number>>(new Map());

  // function to get the next available table number
  const getNextAvailableTableNumber = () => {
    // Extract all current table numbers
    const usedNumbers = Array.from(activeDataTables.values());
    
    // Sort them to find gaps
    usedNumbers.sort((a, b) => a - b);
    
    // Find the first missing number
    for (let i = 0; i < usedNumbers.length; i++) {
      if (usedNumbers[i] !== i) {
        return i; // Found a gap
      }
    }
    // If no gaps, return the next number
    return usedNumbers.length;
  };

  const handleSaveTree = async () => {
    const requestBuilder = new StatefulRequestBuilder(transactionId);
    const statefulRequest = requestBuilder.SaveTreeRequest();
    const response = await statefulAnalysisTree(statefulRequest);
    if (response) {
      showToast("Tree saved successfully", "", "success");
    } else {
      showToast("Failed to save tree", "", "error");
    }
  };

  //Update the analysis/tree name and description.
  const handleAnalysisNameAndDescription = async (newAnalysisName: string, newAnalysisDescription: string) => {
    setAnalysisName(newAnalysisName);
    setAnalysisDescription(newAnalysisDescription);
    
    const requestBuilder = new StatefulRequestBuilder(transactionId);
    const statefulRequest = requestBuilder.UpdateTreeHeaderRequest(newAnalysisName, newAnalysisDescription);
    await statefulAnalysisTree(statefulRequest);
  };

  //handles switching between chart and table
  const toggleView = () => {
    setShowTable((prev) => !prev);
  };

  // Effect to handle FIFO node addition
  useEffect(() => {
    if (!selectedNode) return;

    // First check if the node is already in the table list
    const nodeId = selectedNode.current_node.id!;
    const isNodeAlreadyInTables = selectedNodesToShowTables.some(
      item => item.node.current_node.id === nodeId
    );

    // Only add to activeDataTables if it's a new node
    if (!isNodeAlreadyInTables) {
      setActiveDataTables(prev => {
        const newMap = new Map(prev);
        if (!newMap.has(nodeId)) {
          const nextNumber = getNextAvailableTableNumber();
          newMap.set(nodeId, nextNumber);
        }
        return newMap;
      });
    }

    // Update the node list separately
    setSelectedNodesToShowTables((prevNodes) => {
      const selectedNodeId = nodeId;
      const existingNodeIndex = prevNodes.findIndex(
        (n) => n.node.current_node.id === selectedNodeId
      );

      if (existingNodeIndex !== -1) {
        const updatedNodes = prevNodes.filter((_, index) => index !== existingNodeIndex);
        return [
          { ...prevNodes[existingNodeIndex], loading: nodeDataLoader },
          ...updatedNodes
        ];
      }

      const newNode = { 
        node: selectedNode, 
        loading: nodeDataLoader 
      };

      if (prevNodes.length >= 12) {
        return [newNode, ...prevNodes.slice(0, -1)];
      }

      return [newNode, ...prevNodes];
    });
  }, [selectedNode]);

  const handleClearAllTables = () => {
    // Clear the activeDataTables map completely
    setActiveDataTables(new Map());
    // Clear the selectedNodesToShowTables array
    setSelectedNodesToShowTables([]);
    setIsClearAllTables(true);
  };
  // Effect to handle clearing the activeDataTables map when the selectedNodesToShowTables array is empty
  useEffect(() => {
    if (selectedNodesToShowTables.length === 0) {
      setActiveDataTables(new Map());
    }
  }, [selectedNodesToShowTables.length]);

  return (
    <CommentaryContextProvider>
      <AnalysisPanel
        tree={tree}
        analysisName={analysisName}
        analysisDescription={analysisDescription}
        selectedNode={selectedNode}
        nodeDataLoader={nodeDataLoader}
        showTable={showTable}
        toggleView={toggleView}
        handleAnalysisNameAndDescription={handleAnalysisNameAndDescription}
        isAdmin={isAdmin}
        treePannelLoader={treePannelLoader}
        setSelectedNodesToShowTables={setSelectedNodesToShowTables}
        selectedNodesToShowTables={selectedNodesToShowTables}
        isCommentaryVisible={isCommentaryVisible}
        setIsCommentaryVisible={setIsCommentaryVisible}
        nodeInfoMap={nodeInfoMap}
        analysisInfo={tree?.header}
        onToggleChat={onToggleChat}
        isChatOpen={isChatOpen}
        activeDataTables={activeDataTables}
        handleClearAllTables={handleClearAllTables}
        isClearAllTables={isClearAllTables}
        setIsClearAllTables={setIsClearAllTables}
        handleSaveTree={handleSaveTree}
      />
    </CommentaryContextProvider>
  );
};

export default AnalysisPanelContainer;
